import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { CarouselDocumentsComponent } from 'src/app/shared/carousel-documents/carousel-documents.component';
import { ConfirmationComponent } from 'src/app/shared/confirmation/confirmation.component';
import {
  DataSelects,
  DetailDocument,
  DocumentCategory,
} from 'src/app/shared/interfaces/data-selects';
import { RequestInformationComponent } from 'src/app/shared/request-information/request-information.component';
import { GetDataService } from 'src/app/shared/services/get-data.service';
import { PostDataService } from 'src/app/shared/services/post-data.service';
import { PutDataService } from 'src/app/shared/services/put-data.service';
import { RequestFormService } from 'src/app/shared/services/request-form.service';
import { AutoUnsubscribeComponent } from 'src/app/shared/utils/auto-unsubscribe.component';

@Component({
  selector: 'app-step-cargar-documentos',
  templateUrl: './step-cargar-documentos.component.html',
  styleUrls: ['./step-cargar-documentos.component.scss'],
})
export class StepCargarDocumentosComponent
  extends AutoUnsubscribeComponent
  implements OnInit {
  @Input() dataLoadDocuments!: DataSelects;
  @Input() saveRequest!: Observable<boolean>;
  @Output() formValidEvent = new EventEmitter<boolean>();
  @Output() stepLoadDocumentSavedEvent = new EventEmitter<boolean>();

  public formStepLoadDocument!: FormGroup;
  protected _onDestroy = new Subject<void>();
  public documents: DocumentCategory[] = [];
  public cameraMovil!: boolean;
  public referalQr!: string;
  public codeReferral!: string;
  public creditTypeUpdate!: string;
  public enableButtonNext = false;

  private allowedFileTypes = [
    'image/png',
    'image/jpeg',
    'application/pdf',
    'application/x-compressed',
  ];
  private documentsOpenCamera = [
    'IDENTIFICACIÓN FRENTE',
    'IDENTIFICACIÓN REVERSO',
    'LICENCIA FRENTE',
    'LICENCIA REVERSO',
  ];

  constructor(
    private requestFormService: RequestFormService,
    private getDataService: GetDataService,
    private postDataService: PostDataService,
    private putDataService: PutDataService,
    public dialog: MatDialog,
    private activatedRouter: ActivatedRoute
  ) {
    super();

    this.activatedRouter.queryParams.subscribe((params) => {
      this.referalQr = params['referall'];
      this.codeReferral = params['referralCode'];
    });
  }

  ngOnInit(): void {
    this.saveRequest.subscribe((value) => {
      if (value) {
        this.saveDocument();
      }
    });

    this.cameraMovil = /Android|iPhone/i.test(navigator.userAgent);
  }

  ngOnChanges(changes: SimpleChanges) {
    const searchForm = this.requestFormService.getUpdateForm();
    const docTypes = searchForm ? searchForm.documents : [];

    if (!changes['dataLoadDocuments']?.currentValue) {
      return;
    }

    this.documents = this.dataLoadDocuments.documentCategories.filter(
      (op) => op.code === 'MAIN'
    );

    this.documents.forEach((document) => {
      document.documents = document.documents.map((d) => {
        d.canOpenCamera = this.documentsOpenCamera.includes(d.name);
        const doc = docTypes.find((docu) => docu.typeId == d.id);
        if (doc) {
          d.uploaded = true;
          d.scanned = doc.scanned;
          d.fileName = doc.fileName;
        }
        return d;
      });
    });
    this.checkEvent();
  }

  checkEvent() {
    const docUpload = this.documents.every((doc) =>
      doc.documents.every((d) => d.uploaded)
    );
    this.formValidEvent.emit(docUpload);
  }

  loadDocument(event: any, document: DetailDocument) {
    const fileType = event.target.files[0].type;
    const fileMimeType = event.target.files[0].size < 5000000;

    if (this.allowedFileTypes.includes(fileType) && fileMimeType) {
      const formData = new FormData();
      formData.append('file', event.target.files[0]);
      document.spinner = true;
      const postSaveDocument$ = this.postDataService
        .postSaveDocument(
          this.getCountry().toUpperCase(),
          this.getApplicationId(),
          document.id,
          formData
        )
        .subscribe({
          next: (resp) => {
            document.spinner = false;
            document.uploaded = true;
            document.fileName = resp.fileName;
            document.scanned = resp.scanned!;
            this.checkEvent();
          },
          error: (err) => {
            if (
              err.status === 400 &&
              err.error.message === "You can't make another try"
            ) {
              document.uploaded = true;
            }
          },
        });
      this.subscriptions.push(postSaveDocument$);
    } else {
      this.errorFormatDialog();
    }
  }

  errorFormatDialog() {
    const dropModal = this.dialog.open(ConfirmationComponent, {
      width: 'min(400px,90%)',
      data: {
        title: 'Error de formato.',
        question: `Solo puede agregar documentos con los siguientes formatos: .PDF, .JPG, .PNG, .rar y con un tamaño menor a 10MB. `,
      },
    });

    const closedSubscription$ = dropModal.afterClosed().subscribe(() => []);
    this.subscriptions.push(closedSubscription$);
  }


  viewDocuments(doc: DetailDocument) {
    const getViewDocuments$ = this.getDataService
      .getViewDocuments(
        this.getCountry().toUpperCase(),
        this.getApplicationId(),
        doc.fileName
      )
      .subscribe((resp) => {
        const fileURL = URL.createObjectURL(resp);
        window.open(fileURL);
      });
    this.subscriptions.push(getViewDocuments$);
  }

  saveDocument() {
    const putUpdateRequestLoadDocuments$ = this.putDataService
      .putUpdateRequestLoadDocuments(
        this.getCountry().toUpperCase(),
        this.getApplicationId()
      )
      .subscribe({
        next: (resp) => {
          this.requestFormService.setDataDocumentsOCR(resp);
          this.stepLoadDocumentSavedEvent.emit(true);
        },
        error: (err) => {
          this.openRequestInformationDialog(err);
        },
      });
    this.subscriptions.push(putUpdateRequestLoadDocuments$);
  }

  openRequestInformationDialog(event: HttpErrorResponse) {
    let errorMessage = 'Ha ocurrido un error.';

    const pendingToLoadDocumentsError =
      /^Document Not Found: \d+. Tipo Documento: \d+$/.test(
        event.error.message
      );

    if (event.status === 400 && pendingToLoadDocumentsError) {
      errorMessage = `Debe cargar todos los documentos obligatorios (*) requeridos para poder continuar.`;
    }

    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      title: 'Información de solicitud',
      question: errorMessage,
    };

    this.dialog
      .open(RequestInformationComponent, dialogConfig)
      .afterClosed()
      .subscribe(() => []);
  }

  openViewModelDocuments(doc: DetailDocument) {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.width = 'min(400px,90%)';
    dialogConfig.maxWidth = 'none';
    dialogConfig.data = {
      documents: doc.helpImageUrl,
    };
    this.dialog
      .open(CarouselDocumentsComponent, dialogConfig)
      .afterClosed()
      .subscribe(() => []);
  }

  getApplicationId() {
    return !this.requestFormService.getUpdateForm()
      ? sessionStorage.getItem('applicationId')!
      : this.requestFormService.getUpdateForm().id;
  }

  getCountry() {
    return !this.requestFormService.getUpdateForm()
      ? sessionStorage.getItem('language')!
      : this.requestFormService.getUpdateForm().countryCode.code.toLowerCase();
  }
}
